import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDialogFocus } from '../../v2/useDialogFocus';
import { Link } from 'react-router-dom';
import BusinessDetails from '../../components/shop/settings/BusinessDetails';
import LocationCard from '../../components/shop/settings/LocationCard';
import OperatingHoursCard from '../../components/shop/settings/OperatingHoursCard';
import PrintServicesCard from '../../components/shop/settings/PrintServicesCard';
import BillingDetailsCard from '../../components/shop/settings/BillingDetailsCard';
import CurrentPlanCard from '../../components/shop/settings/CurrentPlanCard';
import SubscriptionActions from '../../components/shop/settings/SubscriptionActions';
import '../../v2/workflows.css';
import InkCompatibilityCard from '../../components/shop/settings/InkCompatibilityCard';
import { useAuth } from '../../contexts/AuthContext';
import { ref as dbRef, update } from "firebase/database";
import { auth, database } from '../../firebase/firebaseConfig';
import CurrentSpecialsCard from '../../components/shop/settings/CurrentSpecialsCard';
import {
    EmailAuthProvider,
    reauthenticateWithCredential,
    signOut,
} from "firebase/auth";
import { useNavigate } from 'react-router-dom';
import { getPaystackMode } from '../../utils/getPlanCodes';
import { functionsBaseUrl } from '../../config/runtime';

const Settings = () => {
    const { currentUser, userLoading } = useAuth();

    const [editingSection, setEditingSection] = useState(null);
    const [saveError, setSaveError] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteConfirmation, setDeleteConfirmation] = useState("");
    const closeDeleteModal = useCallback(() => {
        if (isDeleting) return;
        setShowDeleteModal(false);
        setDeletePassword("");
        setDeleteConfirmation("");
    }, [isDeleting]);
    useDialogFocus(showDeleteModal, closeDeleteModal);
    const navigate = useNavigate();

    const [form, setForm] = useState(currentUser);

    const [savedForm, setSavedForm] = useState(form);

    useEffect(() => {
        if (!currentUser) return;
        setForm((previous) => previous?.uid === currentUser.uid ? previous : currentUser);
        setSavedForm((previous) => previous?.uid === currentUser.uid ? previous : currentUser);
    }, [currentUser]);

    const handleEdit = (section) => {
        setEditingSection(section);
    };

    const handleCancel = () => {
        setForm(savedForm);
        setEditingSection(null);
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAddressChange = (field, value) => {
        setForm((prev) => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value,
            },
        }));
    };

    const getChangedFields = (original, updated) => {
        const changes = {};

        Object.keys(updated).forEach((key) => {
            const originalValue = original[key];
            const updatedValue = updated[key];

            if (
                JSON.stringify(originalValue) !== JSON.stringify(updatedValue)
            ) {
                changes[key] = updatedValue;
            }
        });

        return changes;
    };

    const handleSave = async () => {
        setSaveError("");
        try {
            const changedFields = getChangedFields(savedForm, form);

            if (!Object.keys(changedFields).length) {
                setEditingSection(null);
                return;
            }

            const updates = buildUpdatePayload(changedFields);


            await update(
                dbRef(database, `Users/${currentUser.uid}`),
                updates
            );

            setSavedForm(form);
            setEditingSection(null);

        } catch (error) {
            setSaveError("Your changes could not be saved. Check your connection and try again. Your edits are still here.");
        }
    };

    const buildUpdatePayload = (changes) => {
        const updates = {};

        Object.entries(changes).forEach(([key, value]) => {
            if (
                value &&
                typeof value === "object" &&
                !Array.isArray(value)
            ) {
                Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                    updates[`${key}/${nestedKey}`] = nestedValue;
                });
            } else {
                updates[key] = value;
            }
        });

        return updates;
    };


    const handleDeleteAccount = async () => {
        if (deleteConfirmation !== "DELETE") {
            alert("Please type DELETE to confirm.");
            return;
        }

        if (!deletePassword) {
            alert("Please enter your password.");
            return;
        }

        setIsDeleting(true);

        try {
            const credential = EmailAuthProvider.credential(
                auth.currentUser.email.trim().toLowerCase(),
                deletePassword
            );

            await reauthenticateWithCredential(
                auth.currentUser,
                credential
            );

            const idToken = await auth.currentUser.getIdToken();
            const response = await fetch(
                `${functionsBaseUrl}/deleteCurrentUserAccount`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${idToken}`,
                    },
                    body: JSON.stringify({ mode: getPaystackMode() }),
                }
            );

            const payload = await response.json();

            if (!response.ok) {
                throw new Error(payload?.error || "Unable to delete account.");
            }

            await signOut(auth);

            navigate("/");

        } catch (err) {
            console.error(err);

            if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
                alert("Incorrect password.");
            } else if (err.code === "auth/too-many-requests") {
                alert("Too many attempts. Please try again later.");
            } else {
                alert("Unable to delete account.");
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSignOut = async () => {
        await signOut(auth);
        navigate('/shop-login', { replace: true });
    };

    return (
        <div className="v2-settings-shell">
            <Helmet><title>Account settings - Signet Print</title></Helmet>
            <header className="v2-workspace-header">
                <Link className="v2-brand" to={currentUser?.type === "supplier" || currentUser?.type === "specialist" ? "/supplier/dashboard" : "/shop/dashboard"} aria-label="Signet Print workspace"><span className="v2-brand-mark" aria-hidden="true"><svg viewBox="0 0 52 52" fill="currentColor"><path d="M26 52C40.36 52 52 40.36 52 26S40.36 0 26 0v52Z"/><path d="M0 52c14.36 0 26-11.64 26-26S14.36 0 0 0v52Z"/></svg></span><span>Signet<span>Print</span></span></Link>
                <div className="v2-workspace-identity"><span className="v2-identity-mark">{(currentUser?.shopName || "S").slice(0, 1).toUpperCase()}</span><span className="v2-identity-name">{currentUser?.shopName || "Business account"}</span><span className="v2-role-label">Account</span></div>
                <div className="v2-header-actions"><Link className="v2-text-action" to={currentUser?.type === "supplier" || currentUser?.type === "specialist" ? "/supplier/dashboard" : "/shop/dashboard"}>Back to workspace</Link><button className="v2-text-action" type="button" onClick={handleSignOut}>Sign out</button></div>
            </header>
            {userLoading || !currentUser ? <div className="v2-loading-screen">Loading account settings…</div> : <main className='v2-settings-main'>
                {saveError && <p role="alert" className="v2-form-error">{saveError}</p>}
                <div className="mb-[24px]">
                    <h1 className="font-semibold text-[24px] md:text-[28px] text-[#212326] mb-[4px]">
                        Profile & Settings
                    </h1>
                    <p className="font-medium text-[14px] text-[#717182]">
                        Update your listing details, contact info, services and business hours.
                    </p>
                </div>

                <div
                    className="flex flex-col gap-[20px]"
                >
                    {/* BusinessDetails */}
                    <BusinessDetails
                        type={currentUser?.type}
                        form={form}
                        isEditing={editingSection === "businessDetails"}
                        onEdit={() => handleEdit("businessDetails")}
                        onCancel={handleCancel}
                        onSave={handleSave}
                        onChange={handleChange}
                    />

                    {/* LocationCard */}
                    <LocationCard
                        type={currentUser?.type}
                        form={form}
                        isEditing={editingSection === "location"}
                        onEdit={() => handleEdit("location")}
                        onCancel={handleCancel}
                        onSave={handleSave}
                        onChange={handleAddressChange}
                    />

                    {/* PrintServicesCard */}
                    <PrintServicesCard
                        type={currentUser?.type}
                        form={form}
                        setForm={setForm}
                        isEditing={editingSection === "printServices"}
                        onEdit={() => handleEdit("printServices")}
                        onCancel={handleCancel}
                        onSave={handleSave}
                    />

                    {/* Ink Compatibility */}
                    {
                        (currentUser?.type === "printShop" || currentUser?.type === "specialist" || currentUser?.type === "supplier") && (
                            <InkCompatibilityCard
                                form={form}
                                setForm={setForm}
                                isEditing={editingSection === "inkCompatibility"}
                                onEdit={() => handleEdit("inkCompatibility")}
                                onCancel={handleCancel}
                                onSave={handleSave}
                                type={currentUser?.type}
                            />
                        )
                    }



                    {/* OperatingHoursCard */}
                    <OperatingHoursCard
                        type={currentUser?.type}
                        form={form}
                        setForm={setForm}
                        isEditing={editingSection === "operatingHours"}
                        onEdit={() => handleEdit("operatingHours")}
                        onCancel={handleCancel}
                        onSave={handleSave}
                    />

                    {/* CurrentSpecialsCard */}
                    <CurrentSpecialsCard
                        type={currentUser?.type}
                        form={form}
                        setForm={setForm}
                        isEditing={editingSection === "currentSpecials"}
                        onEdit={() => handleEdit("currentSpecials")}
                        onCancel={handleCancel}
                        onSave={handleSave}
                    />

                    {/* CurrentPlanCard */}
                    <CurrentPlanCard
                        accountUser={currentUser}
                        userType={currentUser?.type || "printShop"}
                        status={currentUser?.plan?.isActive || false}
                        subscriptionStatus={currentUser?.plan?.subscriptionStatus}
                        nextBillingDate={currentUser?.plan?.nextBillingDate || null}
                        trialEndsAt={currentUser?.plan?.trialEndsAt || null}
                    />

                    {/* BillingDetailsCard */}
                    <BillingDetailsCard
                        accountUser={currentUser}
                        nextBillingDate={
                            currentUser?.plan?.nextBillingDate
                                ? currentUser.plan.nextBillingDate
                                : null
                        }

                        paymentMethod={
                            currentUser?.plan?.cardDetails?.last4
                                ? `${currentUser?.plan?.cardDetails?.brand || currentUser?.plan?.cardDetails?.cardType || "Card"} •••• ${currentUser.plan.cardDetails.last4}`
                                : "No card added yet"
                        }

                        planStarted={
                            currentUser?.plan?.planStartedAt
                                ? currentUser.plan.planStartedAt
                                : null
                        }

                        status={currentUser?.plan?.isActive || false}
                        subscriptionStatus={currentUser?.plan?.subscriptionStatus}
                        trialStartedAt={currentUser?.plan?.trialStartedAt || null}
                        trialEndsAt={currentUser?.plan?.trialEndsAt || null}
                    />

                    <SubscriptionActions user={currentUser} />

                    <div className="mt-8 rounded-xl border border-red-200 bg-red-50 p-6">
                        <h2 className="text-lg font-semibold text-red-700">
                            Danger Zone
                        </h2>

                        <p className="mt-2 text-sm text-red-600">
                            Permanently delete your account, business listing and subscription.
                            This action cannot be undone.
                        </p>

                        <button
                            onClick={(event) => {
                                event.currentTarget.focus();
                                setShowDeleteModal(true);
                            }}
                            disabled={isDeleting}
                            className={`mt-5 rounded-lg text-md px-5 py-2 text-white font-semibold transition ${isDeleting
                                ? "bg-red-400 cursor-not-allowed"
                                : "bg-red-600 hover:bg-red-700"
                                }`}
                        >
                            Delete Account
                        </button>
                    </div>

                </div>

                {showDeleteModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                        <div role="dialog" aria-modal="true" aria-labelledby="delete-account-title" className="w-full max-w-md rounded-xl bg-white p-6">
                            <h2 id="delete-account-title" className="text-xl font-semibold text-red-600">
                                Delete Account
                            </h2>

                            <p className="mt-3 text-sm text-gray-600">
                                This will permanently delete your account, business listing and
                                subscription. This action cannot be undone.
                            </p>

                            <div className="mt-5">
                                <label className="mb-1 block text-sm font-medium">
                                    Password
                                </label>

                                <input
                                    type="password"
                                    aria-label="Password"
                                    value={deletePassword}
                                    onChange={(e) =>
                                        setDeletePassword(e.target.value)
                                    }
                                    className="w-full rounded-lg border px-3 py-2"
                                    placeholder="Enter your password"
                                />
                            </div>

                            <div className="mt-4">
                                <label className="mb-1 block text-sm font-medium">
                                    Type <strong>DELETE</strong> to confirm
                                </label>

                                <input
                                    aria-label="Type DELETE to confirm"
                                    value={deleteConfirmation}
                                    onChange={(e) =>
                                        setDeleteConfirmation(e.target.value)
                                    }
                                    className="w-full rounded-lg border px-3 py-2"
                                    placeholder="DELETE"
                                />
                            </div>

                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    className="rounded-lg border px-4 py-2"
                                    onClick={closeDeleteModal}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={handleDeleteAccount}
                                    disabled={
                                        isDeleting ||
                                        deleteConfirmation !== "DELETE"
                                    }
                                    className="rounded-lg bg-red-600 px-4 py-2 text-white disabled:opacity-50"
                                >
                                    {isDeleting ? "Deleting..." : "Delete Account"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>}
        </div>
    );

}

export default Settings;
